import React, { useMemo, useCallback } from 'react'
import {
	LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
	ReferenceDot
} from 'recharts'
import useMediaQuery from '../../store/hooks/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks/hooks';
import { SINGLE_RESPONSIVE_CONTAINER_MIN_SIZE } from '../../constants/constants';


const GasRCChart: React.FC = () => {
	const isMobile = useMediaQuery('(max-width: 900px)');
	const secondWell = useAppSelector(state => state.app.secondWellIsOpen);
	const results1 = useAppSelector(state => state.gasRateCriticalCalculator.results1);
	const results2 = useAppSelector(state => state.gasRateCriticalCalculator.results2);

	const isEmpty = (!results1?.p || results1.p.length === 0);
	const { minX, maxX, minY, maxY, xTicks, yTicks } = useMemo(() => {
		if (isEmpty) {
			const minX = 0, maxX = 700, minY = 0, maxY = 350;
			const xTicks = Array.from({ length: 5 }, (_, i) => minX + (maxX - minX) * i / 4);
			const yTicks = Array.from({ length: 5 }, (_, i) => minY + (maxY - minY) * i / 4);
			return { minX, maxX, minY, maxY, xTicks, yTicks };
		}

		const xValues = results1.q.map(Number);
		const yValues = results1.p.map(Number);

		if (secondWell && results2?.q && results2?.p) {
			const xValues2 = results2.q.map(Number);
			const yValues2 = results2.p.map(Number);
			xValues.push(...xValues2);
			yValues.push(...yValues2);
		}

		const minX = Math.min(...xValues);
		const maxX = Math.max(...xValues);
		const minY = Math.min(...yValues);
		const maxY = Math.max(...yValues);
		const xTicks = Array.from({ length: 5 }, (_, i) => minX + (maxX - minX) * i / 4);
		const yTicks = Array.from({ length: 5 }, (_, i) => minY + (maxY - minY) * i / 4);

		return { minX, maxX, minY, maxY, xTicks, yTicks };
	}, [results1, results2, isEmpty, secondWell]);

	const { t } = useTranslation('ipr');

	const data1 = useMemo(() => {
		if (!results1 || !results1.p || !results1.q) {
			return [];
		}
		return results1.p.map((p, index) => ({ x: Number(results1.q[index]), y: Number(p) }));
	}, [results1]);

	const data2 = useMemo(() => {
		if (!secondWell || !results2 || !results2.p || !results2.q) {
			return [];
		}
		return results2.p.map((p, index) => ({ x: Number(results2.q[index]), y: Number(p) }));
	}, [results2, secondWell]);

	const resPoint = useMemo(() => {
		if (
			results1?.point?.p == null ||
			results1.point.q == null ||
			results1.point.v == null
		) {
			return null;
		}

		return {
			x: Number(results1.point.q),
			y: Number(results1.point.p),
			v: Number(results1.point.v),
		};
	}, [results1]);

	const resPoint2 = useMemo(() => {
		if (
			results2?.point?.p == null ||
			results2.point.q == null ||
			results2.point.v == null
		) {
			return null;
		}

		return {
			x: Number(results2.point.q),
			y: Number(results2.point.p),
			v: Number(results2.point.v),
		};
	}, [results2]);

	const renderLegend = useCallback((props: any) => {
		const { payload } = props;
		return (
			<ul style={{
				display: 'flex',
				flexDirection: isMobile ? 'row' : 'column',
				flexWrap: 'wrap',
				gap: '8px',
				listStyle: 'none',
				margin: 0,
				padding: 0,
				justifyContent: isMobile ? 'center' : 'flex-start',
			}}>
				{payload.flatMap((entry: any, index: number) => {
					return [
						<li key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 14 }}>
							<svg width="28" height="14" style={{ flexShrink: 0 }}>
								<line
									x1={0} y1={7} x2={28} y2={7}
									stroke={entry.color}
									strokeWidth={entry.payload?.strokeWidth ?? 2}
									strokeDasharray={entry.payload?.strokeDasharray}
								/>
							</svg>
							<span>{entry.value}</span>
						</li>
					];
				})}
				{resPoint !== null && (
					<li style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 14 }}>
						<svg width="28" height="14" style={{ flexShrink: 0 }}>
							<circle cx={14} cy={7} r={5} fill="red" />
						</svg>
						<span>{`v: ${resPoint.v.toFixed(2)}`}</span>
					</li>
				)}
				{resPoint2 !== null && (
					<li style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 14 }}>
						<svg width="28" height="14" style={{ flexShrink: 0 }}>
							<circle cx={14} cy={7} r={5} fill="green" />
						</svg>
						<span>{`v: ${resPoint2.v.toFixed(2)}`}</span>
					</li>
				)}
			</ul>
		);
	}, [isMobile, resPoint, resPoint2]);

	return (
		<div className="grc__chart_container round-corner-container">
			{/* {(isLoading || isEmpty) && (
				<div className="ipr__chart-overlay">
					<span>{isLoading ? tCommon('loading') : tCommon('no_data')}</span>
				</div>
			)} */}
			<ResponsiveContainer width="100%" height={300} {...SINGLE_RESPONSIVE_CONTAINER_MIN_SIZE}>
				<LineChart data={isEmpty ? [{ x: minX, y: minY }] : []} margin={{
					top: 20,
					right: 30,
					left: 25,
					bottom: 40
				}}>
					<CartesianGrid strokeDasharray="0"
						horizontal={true}
						vertical={true}
					/>
					<XAxis
						axisLine={true}
						dataKey="x"
						domain={[minX, maxX]}
						label={{ value: t('chart_labels.x'), position: 'insideBottom', offset: -10 }}
						type="number"
						ticks={xTicks}
						tickFormatter={(value: number) => value.toFixed(0)}
					/>
					<YAxis
						axisLine={true}
						domain={[minY, maxY]}
						ticks={yTicks}
						label={{ value: t('chart_labels.y'), angle: -90, position: 'insideLeft', offset: -17 }}
						tickFormatter={(value: number) => value > 1000 ? value.toExponential(2) : value.toFixed(2)}
					/>
					<Tooltip
						// labelFormatter={tooltipLabelFormatter}
						// formatter={tooltipFormatter}
						wrapperStyle={{ pointerEvents: 'none' }}
						isAnimationActive={false}
					/>
					<Legend
						content={renderLegend}
						layout={isMobile ? 'horizontal' : 'vertical'}
						verticalAlign={isMobile ? 'bottom' : 'top'}
						align={isMobile ? 'center' : 'right'}
					/>

					<Line
						type="monotone"
						data={data1}
						dataKey="y"
						stroke={isEmpty ? "transparent" : "#4c23deff"}
						strokeWidth={3}
						name={t('common:well1_title')}
						dot={true}
						activeDot={false}
						legendType="plainline"
					/>
					<Line
						type="monotone"
						data={data2}
						dataKey="y"
						stroke={isEmpty ? "transparent" : "rgb(222, 35, 125)"}
						strokeWidth={3}
						name={t('common:well2_title')}
						dot={true}
						activeDot={false}
						legendType="plainline"
					/>
					{resPoint && (
						<ReferenceDot
							x={resPoint.x}
							y={resPoint.y}
							r={5}
							fill="red"
							stroke="none"
							zIndex={601}
						/>
					)}
					{resPoint2 && (
						<ReferenceDot
							x={resPoint2.x}
							y={resPoint2.y}
							r={5}
							fill="green"
							stroke="none"
							zIndex={601}
						/>
					)}
				</LineChart>
			</ResponsiveContainer>
		</div >
	)
}

export default React.memo(GasRCChart)